<template>
    <div class="detailed-report">
        <div :class="{'detailed-report__head': true, 'detailed-report__head--isMobile': isDisplayMobile}">
            <VJoyCheckbox
                data-testid="toggle-all-weekdays"
                :label="t('activity-tracker.mark.all.weekdays.as.worked')"
                name="all-weeks-worked"
                :checked="hasCheckedAllWeekDaysAsWorked"
                @update:checked="toggleAllWeekDaysHasWorked"
            />
            <VJoyWrapper :class="{'detailed-report__head-wrapper': true, 'detailed-report__head-wrapper--isMobile': isDisplayMobile}">
                <div>
                    <div class="detailed-report__head-title">{{ t('activity-tracker.detailed.total') }}</div>
                    <div data-testid="total-time-worked" class="detailed-report__head-earnings">
                        {{ totalAmountWorked }}
                        {{ t(`activity-tracker.icu.detailed.${temporalUnit}`, {totalAmountWorked}) }}
                    </div>
                </div>
                <div v-show="!isDisplayMobile && totalAmountInFreelancerCurrency">
                    <div class="detailed-report__head-title">{{ t('activity-tracker.detailed.invoiced') }}</div>
                    <div class="detailed-report__head-earnings">
                        {{ totalAmountExcludingServiceFeesIncludingTaxes }}
                    </div>
                </div>

                <div>
                    <div
                        :class="{
                            'detailed-report__head-title': true,
                            'detailed-report__head-title--isMobile': isDisplayMobile,
                            'detailed-report__head-title--isMultiCurrency': totalAmountInFreelancerCurrency,
                        }"
                    >
                        <div v-show="isDisplayMobile && totalAmountInFreelancerCurrency">
                            <div class="detailed-report__head-title">{{ t('activity-tracker.detailed.invoiced') }}</div>
                            <div class="detailed-report__head-earnings">
                                {{ totalAmountExcludingServiceFeesIncludingTaxes }}
                            </div>
                        </div>
                        <div>
                            <VJoyTooltip
                                :delay="0"
                                class="detailed-report__head-tooltip"
                                variant="secondary"
                                append-to="body"
                                :tooltip-width="305"
                                event="click"
                                position="bottom-end"
                            >
                                <VJoyText>
                                    {{ $t('activity-tracker.detailed.earnings') }}
                                    <VJoyIcon name="question-circle" role="button" />
                                </VJoyText>
                                <template #tooltip-content><MonthlyEarnings /></template>
                            </VJoyTooltip>

                            <div v-if="totalAmountInFreelancerCurrency" class="detailed-report__head-earnings">
                                ≈ {{ totalAmountExcServiceFeesIncTaxesInFreelancerCurrency }}
                            </div>
                            <div v-else class="detailed-report__head-earnings">
                                {{ totalAmountExcludingServiceFeesIncludingTaxes }}
                            </div>
                        </div>
                    </div>
                </div>
            </VJoyWrapper>
        </div>

        <div v-for="(week, index) in r$.weeksOfTheMonth.$each" :key="index">
            <details>
                <summary data-testid="week-summary" :class="{withBorderRadius: index === r$.weeksOfTheMonth.$each.length - 1}">
                    <div class="summary">
                        <div class="summary__weeks">
                            <span v-if="!isDisplayMobile">{{ t('activity-tracker.detailed.week') }} {{ index + 1 }} ·</span>
                            <p :class="{isBold: isDisplayMobile}">{{ week.$value.weekRangeText }}</p>
                        </div>
                        <div class="summary__worked-time">
                            <VJoyBadge :variant="amountOfWorkPerWeek[index] === 0 ? 'yellow' : 'dark-blue'">
                                {{ amountOfWorkPerWeek[index] }}
                                {{ t(`activity-tracker.icu.detailed.${temporalUnit}`, {totalAmountWorked: amountOfWorkPerWeek[index]}) }}
                            </VJoyBadge>
                        </div>
                        <VJoyIcon name="chevron-down" :class="{summary__icon: true, 'summary__icon--isMobile': isDisplayMobile}" />
                    </div>
                </summary>

                <div v-for="day in week.daysOfWeek.$each" :key="day.$value.dayNumber">
                    <div
                        :class="{
                            weekdays: true,
                            'weekdays--isMobile': isDisplayMobile && checkedHandler(day.$value),
                            'weekdays--hasError': day.workedHours.$error && checkedHandler(day.$value),
                        }"
                        @click="onSelectDay(!day.$value.hasWorked, day.$value)"
                    >
                        <div class="weekdays__days">
                            <div class="weekdays__days-checkbox-wrapper">
                                <VJoyCheckbox
                                    data-testid="day-checkbox"
                                    :checked="checkedHandler(day.$value)"
                                    @click.stop
                                    @update:checked="onSelectDay($event, day.$value)"
                                />
                                <div>
                                    <div class="weekdays__day">{{ day.$value.dayName }}</div>
                                    <div class="weekdays__month">
                                        <div>{{ day.$value.monthName }}, {{ day.$value.dayNumber }}</div>
                                    </div>
                                </div>
                            </div>
                            <VJoyFormError
                                v-if="!isDisplayMobile && day.workedHours.$error && checkedHandler(day.$value)"
                                class="weekdays__days-hourly-error"
                                :errors="day.workedHours.$errors"
                            />
                        </div>

                        <div
                            v-if="!checkedHandler(day.$value)"
                            :class="{'weekdays__not-worked': true, 'weekdays__not-worked--isMobile': isDisplayMobile && !checkedHandler(day.$value)}"
                        >
                            {{ t('activity-tracker.detailed.not.worked') }}
                        </div>

                        <div v-else :class="{weekdays__worked: true, 'weekdays__worked--isMobile': isDisplayMobile && checkedHandler(day.$value)}">
                            <div v-if="temporalUnit === TemporalUnit.DAILY" @click.stop>
                                <VJoySelect v-model="day.workedDays.$value">
                                    <option value="1">{{ t('activity-tracker.detailed.full.day') }}</option>
                                    <option value="0.5">{{ t('activity-tracker.detailed.half.day') }}</option>
                                </VJoySelect>
                            </div>

                            <div
                                v-if="temporalUnit === TemporalUnit.HOURLY"
                                :class="{weekdays__hourly: true, 'weekdays__hourly--width-margin': isDisplayMobile}"
                                @click.stop
                            >
                                <VJoyInputNumber
                                    v-model.number="day.workedHours.$value"
                                    :max="24"
                                    :min="0.5"
                                    step="any"
                                    data-testid="day-hourly-counter"
                                    :name="`counter-${index}-${day.$value.dayNumber}`"
                                    size="medium"
                                    :invalid="day.workedHours.$error"
                                    :unit="t('general.hours')"
                                    @mousedown="inputCurrentlyClicked = true"
                                    @mouseup="inputCurrentlyClicked = false"
                                />
                                <VJoyFormError v-if="isDisplayMobile && day.workedHours.$error" :errors="day.workedHours.$errors" />
                            </div>
                        </div>
                        <ApplyDropdown
                            :active-dropdown="activeDropdown"
                            :day="day.$value"
                            :weeks-of-the-month="weeksOfTheMonth"
                            :is-checked="checkedHandler(day.$value)"
                            @click.stop
                            @toggle-dropdown="toggleApplyDropdown"
                            @close-dropdown="(daysSelected) => closeApplyDropdown(day.$value, daysSelected)"
                        />
                    </div>
                </div>
            </details>
        </div>

        <div class="detailed-report__work-description">
            <VJoyTextarea
                v-model="workDescription"
                optional-label="optional"
                :label="t('activity-tracker.simple.work.description.label')"
                :placeholder="t('activity-tracker.simple.work.description.placeholder')"
                :maxlength="5120"
            />
        </div>
    </div>

    <VJoyDialog
        ref="clearMonthDataDialog"
        class="detailed-report__clear-month-dialog"
        pattern-confirm
        size="large"
        data-testid="confirmation-dialog-clear-month-data"
        :confirm-text="t('activity-tracker.clear.month.data.dialog.confirm.cta')"
        :cancel-text="t('general.cancel')"
        :focus-trap="false"
        @dialog:confirm="toggleAllWeekdaysHasNotWorked"
        @dialog:hide="openClearMonthDataDialog = false"
    >
        <template #dialog-header>{{ $t('activity-tracker.clear.month.data.dialog.header') }}</template>
        <template #dialog-body>{{ $t('activity-tracker.clear.month.data.dialog.body') }}</template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref, watch} from 'vue';
    import {between, required} from '@regle/rules';
    import {format, getDate, parseISO} from 'date-fns';
    import {storeToRefs} from 'pinia';
    import {useFormat, useTranslation} from '#imports';
    import {useScopedRegle} from '@malt/forms';
    import {useDisplayMobile} from '@malt/utils';
    import {
        VJoyBadge,
        VJoyCheckbox,
        VJoyDialog,
        VJoyFormError,
        VJoyIcon,
        VJoyInputNumber,
        VJoySelect,
        VJoyText,
        VJoyTextarea,
        VJoyTooltip,
        VJoyWrapper,
    } from '@maltjoy/core-vue';
    import type {WorkAmountResource} from '@api';
    import ApplyDropdown from './ApplyDropdown.vue';
    import MonthlyEarnings from './MonthlyEarnings.vue';
    import {useActivityTrackerStore, useEarningsOfTheMonthStore} from '@/common/stores';
    import type {Day, Days, Week} from '@/common/types';
    import {TemporalUnit, Weekend} from '@/common/types';
    import {halfHourIncrement} from '@/common/validators';
    import {useGetAmountOfTimeWorked, useGetWeeksOfMonth} from '@/features/detailed-report/composables';

    const {t} = useTranslation();
    const {isDisplayMobile} = useDisplayMobile();
    const activityTrackerStore = useActivityTrackerStore();
    const earningsOfTheMonthStore = useEarningsOfTheMonthStore();
    const {formatAmount} = useFormat();
    const {setDetailedReportWorkAmounts} = activityTrackerStore;
    const {year, month, unit, detailedReportWorkAmounts, workDescription} = storeToRefs(activityTrackerStore);
    const {amountExcludingServiceFeesIncludingTaxes, currency, freelancerCurrency, totalAmountInFreelancerCurrency} =
        storeToRefs(earningsOfTheMonthStore);
    const {weeksOfTheMonth, daysOfTheMonth} = useGetWeeksOfMonth(month.value - 1, year.value);

    const activeDropdown = ref<{dayNumber: number} | null>(null);
    const temporalUnit = ref<TemporalUnit>(unit.value as TemporalUnit);
    const openClearMonthDataDialog = ref<boolean>(false);

    const {totalAmountWorked, amountOfWorkPerWeek} = useGetAmountOfTimeWorked(daysOfTheMonth, weeksOfTheMonth, temporalUnit);

    const {r$} = useScopedRegle(
        {weeksOfTheMonth},
        {
            weeksOfTheMonth: {
                $each: {
                    daysOfWeek: {
                        $each: {
                            workedHours: {
                                required,
                                between: between(0.5, 24),
                                halfHourIncrement,
                            },
                        },
                    },
                },
            },
        },
    );

    const clearMonthDataDialog = ref<InstanceType<typeof VJoyDialog>>();

    watch(
        [openClearMonthDataDialog, clearMonthDataDialog],
        async () => {
            if (openClearMonthDataDialog.value) {
                await clearMonthDataDialog.value?.show();
            } else {
                await clearMonthDataDialog.value?.hide();
            }
        },
        {immediate: true},
    );

    const totalAmountExcludingServiceFeesIncludingTaxes = computed(() =>
        formatAmount(currency.value!, amountExcludingServiceFeesIncludingTaxes.value!, {decimals: 2}),
    );

    const totalAmountExcServiceFeesIncTaxesInFreelancerCurrency = computed(() =>
        formatAmount(freelancerCurrency.value!, totalAmountInFreelancerCurrency.value!, {decimals: 2}),
    );

    function mapWorkAmounts(workAmounts: {[key: string]: {amount: number}}) {
        return Object.keys(workAmounts).length > 0
            ? Object.keys(workAmounts).map((date) => ({
                  day: getDate(parseISO(date)),
                  ...workAmounts[date],
              }))
            : [];
    }

    function fillWeeksOfTheMonth() {
        weeksOfTheMonth.value = weeksOfTheMonth.value.map((week: Week) => {
            return {
                ...week,
                daysOfWeek: week.daysOfWeek.map((day: Day) => {
                    const hasMatchingDay =
                        detailedReportWorkAmounts.value &&
                        mapWorkAmounts(detailedReportWorkAmounts.value).find((d: {day: number; amount: number}) => d.day === day.dayNumber);

                    const isHourlyAndMatching = hasMatchingDay && temporalUnit.value === TemporalUnit.HOURLY;
                    const isDailyAndMatching = hasMatchingDay && temporalUnit.value === TemporalUnit.DAILY;

                    return {
                        ...day,
                        workedHours: isHourlyAndMatching && hasMatchingDay.amount !== 0 ? hasMatchingDay.amount : day.workedHours,
                        workedDays: isDailyAndMatching && hasMatchingDay.amount > 0 ? hasMatchingDay.amount.toString() : day.workedDays,
                        hasWorked: (isDailyAndMatching || isHourlyAndMatching) && hasMatchingDay.amount > 0,
                    };
                }),
            };
        }) as {daysOfWeek: Days; weekRangeText: string}[];
    }

    onMounted(() => {
        fillWeeksOfTheMonth();
    });

    const monthlyWorkAmount = computed(() => {
        return daysOfTheMonth.value
            .filter((day: Day) => day.hasWorked)
            .reduce((acc: {[key: string]: WorkAmountResource}, day: Day) => {
                const formattedDate = format(new Date(day.fullDate), 'yyyy-MM-dd');

                acc[formattedDate] = {
                    amount: temporalUnit.value === TemporalUnit.DAILY ? Number(day.workedDays) : day.workedHours,
                };

                return acc;
            }, {});
    });

    watch(monthlyWorkAmount, () => {
        setDetailedReportWorkAmounts(monthlyWorkAmount.value);
    });

    function toggleApplyDropdown(dayNumber: number) {
        activeDropdown.value = activeDropdown.value?.dayNumber === dayNumber ? null : {dayNumber};
    }

    function closeApplyDropdown(daySelected?: Day, daysSelected?: string[]) {
        if (daysSelected && daySelected) {
            weeksOfTheMonth.value = weeksOfTheMonth.value.map((week: Week) => {
                return {
                    ...week,
                    daysOfWeek: week.daysOfWeek.map((day: Day) => {
                        const isMatchingDaySelected = daysSelected.includes(day.weeklyDailyIndex.toString());

                        return {
                            ...day,
                            workedHours: isMatchingDaySelected ? daySelected.workedHours : day.workedHours,
                            workedDays: isMatchingDaySelected ? daySelected.workedDays : day.workedDays,
                            hasWorked: isMatchingDaySelected || day.hasWorked,
                        };
                    }),
                };
            });
        }

        activeDropdown.value = null;
    }

    function checkedHandler(value: Day) {
        return daysOfTheMonth.value.some((day: Day) => day.dayNumber === value.dayNumber && day.hasWorked);
    }

    function isWeekend(day: Day) {
        return day.weeklyDailyIndex === Weekend.SATURDAY || day.weeklyDailyIndex === Weekend.SUNDAY;
    }

    const hasCheckedAllWeekDaysAsWorked = computed(() => {
        return daysOfTheMonth.value.find((day: Day) => !day.hasWorked && !isWeekend(day)) === undefined && !openClearMonthDataDialog.value;
    });

    function onOpenClearMonthDataDialog() {
        openClearMonthDataDialog.value = true;
    }

    function mapWeeksOfTheMonth(weeks: Week[], value: boolean) {
        return weeks.map((week: Week) => {
            return {
                ...week,
                daysOfWeek: week.daysOfWeek.map((day: Day) => {
                    if (isWeekend(day)) {
                        return day;
                    }
                    return {...day, hasWorked: value};
                }),
            };
        });
    }

    function toggleAllWeekdaysHasNotWorked() {
        weeksOfTheMonth.value = mapWeeksOfTheMonth(weeksOfTheMonth.value, false);
        openClearMonthDataDialog.value = false;
    }

    function toggleAllWeekDaysHasWorked(value: boolean) {
        if (!value) {
            onOpenClearMonthDataDialog();
        }
        weeksOfTheMonth.value = mapWeeksOfTheMonth(weeksOfTheMonth.value, true);
        closeApplyDropdown();
    }

    // To avoid selecting day when the user start clicking on input and release outside of it (in the line)
    const inputCurrentlyClicked = ref<boolean>(false);

    function onSelectDay(event: boolean, value: Day) {
        if (inputCurrentlyClicked.value) {
            inputCurrentlyClicked.value = false;
            return;
        }
        if (activeDropdown.value) {
            closeApplyDropdown();
        }

        const checked = event;

        weeksOfTheMonth.value = weeksOfTheMonth.value.map((week: Week) => {
            return {
                ...week,
                daysOfWeek: week.daysOfWeek.map((day: Day) => {
                    return {
                        ...day,
                        hasWorked: day.dayNumber === value.dayNumber ? checked : day.hasWorked,
                    };
                }),
            };
        });
    }

    defineExpose({
        weeksOfTheMonth,
        openClearMonthDataDialog,
        activeDropdown,
    });
</script>

<style lang="scss" scoped>
    details > summary {
        list-style: none;
        border-bottom: 1px solid var(--joy-color-neutral-30);
        border-left: 1px solid var(--joy-color-neutral-30);
        border-right: 1px solid var(--joy-color-neutral-30);
        cursor: pointer;
        background-color: var(--joy-color-neutral-0);
        padding: var(--joy-core-spacing-4) var(--joy-core-spacing-5);

        .summary__icon {
            transition: var(--joy-transition-duration-short);
            transform: rotate(0deg);
        }
    }

    details > summary::-webkit-details-marker {
        display: none;
    }

    details[open] {
        background-color: var(--joy-color-neutral-0);
    }

    details[open] summary {
        border-bottom: 1px solid var(--joy-color-neutral-30);
        border-radius: 0px;
        .summary__icon {
            transition: var(--joy-transition-duration-short);
            transform: rotate(-180deg);
        }
    }

    .withBorderRadius {
        border-radius: 0px 0px var(--joy-core-spacing-2) var(--joy-core-spacing-2);
    }
    .detailed-report {
        width: 100%;

        &__head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            background-color: var(--joy-color-neutral-0);
            padding: var(--joy-core-spacing-4) var(--joy-core-spacing-5);
            border: 1px solid var(--joy-color-neutral-30);
            border-radius: var(--joy-core-spacing-2) var(--joy-core-spacing-2) 0px 0px;

            &-tooltip {
                .joy-icon {
                    color: var(--joy-color-neutral-40);
                }
                :deep(.joy-text) {
                    color: var(--joy-color-neutral-40);
                }
            }

            :deep(label) {
                font-size: var(--joy-font-size-primary-300);
            }

            &--isMobile {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
            }

            &-wrapper {
                gap: var(--joy-core-spacing-9);

                &--isMobile {
                    gap: var(--joy-core-spacing-8);
                    margin-top: var(--joy-core-spacing-6);
                    width: 100%;
                    justify-content: space-between;
                }
            }

            &-title {
                font-weight: var(--joy-font-weight-normal);
                color: var(--joy-color-neutral-40);

                &--isMobile {
                    display: flex;
                    gap: var(--joy-core-spacing-8);
                    text-align: right;
                }

                &--isMultiCurrency {
                    text-align: left;
                }
            }

            &-earnings {
                color: var(--joy-color-neutral-60);
                font-size: var(--joy-font-size-primary-800);
                font-weight: var(--joy-font-weight-bold);
            }
        }

        &__work-description {
            margin-top: var(--joy-core-spacing-4);
        }

        :deep(label) {
            margin: 0;
            gap: var(--joy-core-spacing-2);
        }
        :deep(.joy-wrapper) {
            margin-bottom: 0px;
        }
    }

    .summary {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: var(--joy-font-size-primary-300);

        &__weeks {
            width: 100%;
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-1);
            white-space: nowrap;

            span {
                font-weight: var(--joy-font-weight-bold);
                letter-spacing: var(--joy-font-letter-spacing-tight);
            }

            p {
                font-weight: var(--joy-font-weight-normal);
            }
        }

        .isBold {
            font-weight: var(--joy-font-weight-bold);
        }

        &__worked-time {
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: flex-end;
            font-weight: var(--joy-font-weight-normal);
            color: var(--joy-color-neutral-50);
        }

        &__icon {
            color: var(--joy-color-neutral-40);
            margin-left: var(--joy-core-spacing-8);

            &--isMobile {
                margin-left: var(--joy-core-spacing-4);
            }
        }
    }

    .weekdays {
        cursor: pointer;
        border-bottom: 1px solid var(--joy-color-neutral-30);
        border-left: 1px solid var(--joy-color-neutral-30);
        border-right: 1px solid var(--joy-color-neutral-30);
        padding: var(--joy-core-spacing-4) var(--joy-core-spacing-5);
        display: flex;
        align-items: center;
        max-height: 64px;

        &--hasError {
            align-items: baseline;
            min-height: 110px;
        }

        &--isMobile {
            display: flex;
            flex-direction: column;
            max-height: 100%;
            align-items: flex-start;
        }

        &__days {
            display: flex;
            position: relative;

            &-checkbox-wrapper {
                display: flex;
                align-items: center;
            }

            &-hourly-error {
                margin: 0;
                width: max-content;
                position: absolute;
                left: 0px;
                top: 50px;
            }
        }

        &__day {
            font-size: var(--joy-font-size-primary-300);
            width: 50px;
        }

        &__month {
            font-size: var(--joy-font-size-primary-200);
            color: var(--joy-color-neutral-40);
        }
        &__not-worked {
            font-size: var(--joy-font-size-primary-300);
            margin-left: var(--joy-core-spacing-11);
            color: var(--joy-color-neutral-40);

            &--isMobile {
                display: flex;
                width: 100%;
                justify-content: flex-end;
            }
        }

        &__worked {
            margin-left: var(--joy-core-spacing-11);
            display: flex;
            align-items: center;
            min-width: fit-content;
            gap: var(--joy-core-spacing-4);

            &--isMobile {
                display: flex;
                width: 100%;
                align-items: stretch;
                flex-direction: column;
                margin-left: 0;
                margin-top: var(--joy-core-spacing-4);
            }
        }

        :deep(.joy-input .joy-input--wrapper) {
            margin: 0;
            min-width: 200px;
        }

        &__hourly {
            :deep(.joy-form-error.joy-has-error) {
                margin: var(--joy-core-spacing-4) 0 0 0;
            }

            &--width-margin {
                margin-bottom: var(--joy-core-spacing-4);
            }
        }
    }
</style>
